import { Divider, IconButton, Link, Typography, useTheme } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { formatDate } from 'framework/utils/date/formatDate';
import {
	CochlearInfoEventExportProp,
	CochlearInfoEventExportProps,
	exportCochlearInfoEvents_handle,
	getCochlearInfoEvents_handle,
	ICochlearInfoEventDto,
	ICochlearSettingsDto,
	IExportCochlearInfoEventsParams,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useState } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { ExportSelectPropsDialog } from 'shared/export/ExportSelectPropsDialog';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { RegisterAsCochlearSpecialistButton } from './cochlear.components';

function createExportRecord(strings: IStrings): Record<CochlearInfoEventExportProp, string> {
	return {
		Date: strings.date,
		Description: strings.description,
		FormattedAddress: strings.address,
		PostalCode: strings.zip,
		RegistrationEmail: strings.email,
		RegistrationUrl: 'URL',
		Title: strings.title,
		Type: strings.type,
	};
}

function filterEvents(events: ICochlearInfoEventDto[], search: string): ICochlearInfoEventDto[] {
	return events.filter(t => search === '' || t.title?.toLowerCase().includes(search.toLowerCase()));
}

type CochlearEventsTabProps = IReloadTriggerProps & {
	reload: VoidFunction;
	settings: ICochlearSettingsDto;
};

export const CochlearEventsTab = ({ reload, reloadTrigger, settings }: CochlearEventsTabProps) => {
	const strings = useLocalization();
	const [data, reloadEvents] = useApiEffect(getCochlearInfoEvents_handle);
	const patientEvents = (data ?? []).filter(t => t.type === 'Patient');
	const audiologistEvents = (data ?? []).filter(t => t.type === 'Audiologist');
	const [search, setSearch] = useState<string>('');
	const fPatientEvents = filterEvents(patientEvents, search);
	const fAudiologistEvents = filterEvents(audiologistEvents, search);
	const { open, cancel } = useDialogsContext(reload);

	const onExport = () => {
		open(
			<ExportSelectPropsDialog<IExportCochlearInfoEventsParams, CochlearInfoEventExportProp>
				open
				fileName={'events.xlsx'}
				close={cancel}
				params={{ fileName: 'events.xlsx', format: 'Xlsx', headers: [], mustApplyFilter: true }}
				downloadF={exportCochlearInfoEvents_handle}
				record={createExportRecord(strings)}
				allProps={CochlearInfoEventExportProps}
				necessaryProps={[]}
				title={`Export events`}
				format='Xlsx'
				mustApplyFilter={true}
				view='cochlear_events'
			/>
		);
	};

	useLazyEffect(() => {
		reloadEvents();
	}, [reloadTrigger]);

	if (data === undefined) {
		return <div></div>;
	}

	return (
		<div
			className='df-col gap-8 w100'
			style={{ padding: '16px' }}>
			<div className='df-row-ac gap-8 w100'>
				<SearchbarX
					placeholder={strings.searchVerb}
					value={search}
					onSearch={val => setSearch(val)}
					style={{ width: 500, height: 36 }}
				/>
				<div className='fg1'></div>
				<RegisterAsCochlearSpecialistButton settings={settings} />
				<IconButton
					size='small'
					color='primary'
					onClick={onExport}>
					<PrintOutlinedIcon />
				</IconButton>
			</div>
			<Divider />
			<Typography style={{ fontSize: 18, marginTop: 16 }}>{strings.patientEventDays}</Typography>
			{fPatientEvents.length === 0 && <em>{strings.noEventsFound}</em>}
			<div
				className='df-row-wrap'
				style={{ rowGap: 12, columnGap: 12 }}>
				{fPatientEvents.map(t => (
					<CochlearInfoEventCard
						item={t}
						key={t.id}
					/>
				))}
			</div>
			<Typography style={{ fontSize: 18, marginTop: 16 }}>{`Professional events`}</Typography>
			{fAudiologistEvents.length === 0 && <em>{strings.noEventsFound}</em>}
			<div
				className='df-row-wrap'
				style={{ rowGap: 12, columnGap: 12 }}>
				{fAudiologistEvents.map(t => (
					<CochlearInfoEventCard
						item={t}
						key={t.id}
					/>
				))}
			</div>
		</div>
	);
};

type CochlearInfoEventCardProps = {
	item: ICochlearInfoEventDto;
};

const CochlearInfoEventCard = ({ item }: CochlearInfoEventCardProps) => {
	const theme = useTheme();
	const strings = useLocalization();

	return (
		<div
			className='df-col gap-4'
			style={{
				border: '1px solid',
				borderRadius: 4,
				borderColor: theme.palette.grey[200],
				padding: 12,
				flexBasis: `calc((100% - 24px) / 3)`,
				maxWidth: `calc((100% - 24px) / 3)`,
				backgroundColor: theme.palette.background.paper,
			}}>
			<div>
				<strong>{item.title}</strong>
				{` - `}
				{item.description}
			</div>
			<div className='df-row-ac gap-4'>
				<EventIcon
					fontSize='small'
					color='action'
				/>
				<div style={{ color: theme.palette.text.secondary }}>{formatDate(item.date)}</div>
			</div>
			<div className='df-row-ac gap-4'>
				<LocationOnOutlinedIcon
					fontSize='small'
					color='action'
				/>
				<div style={{ color: theme.palette.text.secondary }}>{item.address?.formatted}</div>
			</div>
			{item.registrationEmail && (
				<div className='df-row-ac gap-4'>
					<MailOutlineIcon
						fontSize='small'
						color='primary'
					/>
					<Link href={`mailto:${item.registrationEmail}`}>{item.registrationEmail}</Link>
				</div>
			)}
			{item.registrationUrl && (
				<div className='df-row-ac gap-4'>
					<OpenInNewOutlinedIcon
						fontSize='small'
						color='primary'
					/>
					<Link
						href={item.registrationUrl}
						target='_blank'
						rel='noopener noreferrer'>
						{strings.enroll}
					</Link>
				</div>
			)}
		</div>
	);
};
